import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { 
  Sprout, 
  Dumbbell, 
  Flame, 
  Crown,
  ChevronRight,
  Loader2,
  Users,
  Calendar,
  Target
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const levelIcons = {
  principiante: Sprout,
  intermedio: Dumbbell,
  avanzado: Flame,
  goat: Crown
};

const LevelSelectorPage = ({ onLevelSelected }) => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [levels, setLevels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/member/available-programs`, {
        headers: getAuthHeaders(),
      });
      setLevels(response.data.levels);
    } catch (error) {
      console.error("Error fetching levels:", error);
      toast.error("Error al cargar los niveles");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLevel = async () => {
    if (!selectedLevel) return;
    
    // Track is always required (men/women)
    if (!selectedTrack) {
      toast.error("Selecciona Hombre o Mujer");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/member/select-level`,
        { 
          level: selectedLevel, 
          track: selectedTrack
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("¡Nivel seleccionado! Tu programa está listo.");
      if (onLevelSelected) {
        onLevelSelected(response.data);
      }
    } catch (error) {
      console.error("Error selecting level:", error);
      toast.error(error.response?.data?.detail || "Error al seleccionar nivel");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh bg-[#0B0B0B] flex items-center justify-center pt-safe pb-safe">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const levelOrder = ["principiante", "intermedio", "avanzado", "goat"];

  return (
    <div className="min-h-dvh bg-[#0B0B0B] px-4 py-8 pt-safe pb-safe" data-testid="level-selector-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center mx-auto mb-4">
          <Target className="w-8 h-8 text-[#0B0B0B]" />
        </div>
        <h1 className="text-2xl font-heading font-bold text-white mb-2">
          Selecciona tu Nivel
        </h1>
        <p className="text-[#888] text-sm max-w-xs mx-auto">
          Elige el programa que mejor se adapte a tu experiencia
        </p>
      </div>

      {/* Level Cards */}
      <div className="space-y-3 max-w-md mx-auto mb-6">
        {levelOrder.map((levelKey) => {
          const level = levels[levelKey];
          if (!level) return null;
          
          const Icon = levelIcons[levelKey];
          const isSelected = selectedLevel === levelKey;
          const program = level.programs[0];

          return (
            <Card
              key={levelKey}
              className={cn(
                "border-2 transition-all cursor-pointer",
                isSelected
                  ? "border-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-[#2A2A2A] bg-[#121212] hover:border-[#3A3A3A]"
              )}
              onClick={() => {
                setSelectedLevel(levelKey);
                // Reset track when changing level
                setSelectedTrack(null);
              }}
              data-testid={`level-card-${levelKey}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${level.color}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: level.color }} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-heading font-bold text-lg text-white">
                        {level.name}
                      </h3>
                      <ChevronRight 
                        className={cn(
                          "w-5 h-5 transition-transform",
                          isSelected ? "text-[#D4AF37] rotate-90" : "text-[#555]"
                        )} 
                      />
                    </div>
                    <p className="text-sm mb-2 line-clamp-2 text-[#888]">
                      {level.description}
                    </p>
                    
                    {/* Program info badges */}
                    {program && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge 
                          variant="outline" 
                          className="text-xs border-[#3A3A3A] text-[#888]"
                        >
                          <Calendar className="w-3 h-3 mr-1" />
                          {program.weeks} semanas
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className="text-xs border-[#3A3A3A] text-[#888]"
                        >
                          <Dumbbell className="w-3 h-3 mr-1" />
                          {program.days_per_week} días/sem
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>

                {/* Track selector - shown when level is selected */}
                {isSelected && (
                  <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
                    <p className="text-sm text-[#888] mb-3">Selecciona tu programa:</p>
                    <div className="grid grid-cols-2 gap-3">
                      {level.programs.filter(p => p.track === "men" || p.track === "women").length > 0 ? (
                        level.programs
                          .filter(p => p.track === "men" || p.track === "women")
                          .map((prog) => (
                          <button
                            key={prog.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTrack(prog.track);
                            }}
                            className={cn(
                              "p-3 rounded-xl border-2 transition-all text-left",
                              selectedTrack === prog.track
                                ? "border-[#D4AF37] bg-[#D4AF37]/10"
                                : "border-[#2A2A2A] hover:border-[#3A3A3A]"
                            )}
                            data-testid={`track-${prog.track}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="w-4 h-4 text-[#D4AF37]" />
                              <span className="font-bold text-white text-sm">
                                {prog.track === "men" ? "HOMBRES" : "MUJERES"}
                              </span>
                            </div>
                            <p className="text-xs text-[#666] line-clamp-1">
                              {prog.weekly_structure}
                            </p>
                          </button>
                        ))
                      ) : (
                        // Fallback for unisex programs (shouldn't happen with new structure)
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTrack("men");
                            }}
                            className={cn(
                              "p-3 rounded-xl border-2 transition-all text-left",
                              selectedTrack === "men"
                                ? "border-[#D4AF37] bg-[#D4AF37]/10"
                                : "border-[#2A2A2A] hover:border-[#3A3A3A]"
                            )}
                            data-testid="track-men"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="w-4 h-4 text-[#D4AF37]" />
                              <span className="font-bold text-white text-sm">HOMBRES</span>
                            </div>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTrack("women");
                            }}
                            className={cn(
                              "p-3 rounded-xl border-2 transition-all text-left",
                              selectedTrack === "women"
                                ? "border-[#D4AF37] bg-[#D4AF37]/10"
                                : "border-[#2A2A2A] hover:border-[#3A3A3A]"
                            )}
                            data-testid="track-women"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="w-4 h-4 text-[#D4AF37]" />
                              <span className="font-bold text-white text-sm">MUJERES</span>
                            </div>
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Show focus areas when track is selected */}
                    {selectedTrack && (
                      <div className="mt-3">
                        {level.programs.find(p => p.track === selectedTrack)?.focus_areas && (
                          <div className="flex flex-wrap gap-1">
                            {level.programs.find(p => p.track === selectedTrack).focus_areas.map((area, i) => (
                              <Badge 
                                key={i}
                                className="text-xs"
                                style={{ 
                                  backgroundColor: `${level.color}20`,
                                  color: level.color,
                                  border: 'none'
                                }}
                              >
                                {area}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Submit Button */}
      <div className="max-w-md mx-auto">
        <Button
          onClick={handleSelectLevel}
          disabled={!selectedLevel || !selectedTrack || submitting}
          className="w-full h-14 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold text-lg shadow-lg shadow-[#D4AF37]/20 disabled:opacity-50"
          data-testid="confirm-level-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Configurando...
            </>
          ) : (
            "Comenzar Programa"
          )}
        </Button>
        
        <p className="text-center text-[#555] text-xs mt-3">
          Podrás cambiar de nivel más adelante
        </p>
      </div>
    </div>
  );
};

export default LevelSelectorPage;
